// The alarm's audio (architecture sprint-14 §8.1, B-7). One module-level AudioContext. Browsers start it
// suspended until a user gesture, so arm() must be called SYNCHRONOUSLY inside a click/keydown handler.
// Within 10 min of the next mark the loop is pre-scheduled on the AudioContext clock, which keeps running in a
// background tab while timers there fire at most once a minute. Every function is feature-detected and never throws.
import { renderAlarmSound, type AlarmSoundKind } from './alarm-sounds';

/** §8.1: scheduleLoop is used only for delays up to 10 minutes. */
export const MAX_SCHEDULE_DELAY_SEC = 600;
const TEST_PLAY_SEC = 2;

type AudioContextCtor = new () => AudioContext;

let ctx: AudioContext | null = null;
const buffers = new Map<AlarmSoundKind, AudioBuffer>();
let playing: { source: AudioBufferSourceNode; kind: AlarmSoundKind } | null = null;
let scheduled: { source: AudioBufferSourceNode; kind: AlarmSoundKind; startAt: number } | null = null;
const armedListeners = new Set<(armed: boolean) => void>();

function contextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export function audioSupported(): boolean {
  return contextCtor() !== null;
}

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = contextCtor();
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
    ctx.onstatechange = (): void => {
      const armed = isArmed();
      for (const fn of armedListeners) fn(armed);
    };
  } catch {
    ctx = null;
  }
  return ctx;
}

function bufferFor(c: AudioContext, kind: AlarmSoundKind): AudioBuffer | null {
  const cached = buffers.get(kind);
  if (cached && cached.sampleRate === c.sampleRate) return cached;
  try {
    const data = renderAlarmSound(kind, c.sampleRate);
    const buf = c.createBuffer(1, data.length, c.sampleRate);
    buf.getChannelData(0).set(data);
    buffers.set(kind, buf);
    return buf;
  } catch {
    return null;
  }
}

function makeSource(c: AudioContext, kind: AlarmSoundKind, loop: boolean): AudioBufferSourceNode | null {
  const buf = bufferFor(c, kind);
  if (!buf) return null;
  try {
    const source = c.createBufferSource();
    source.buffer = buf;
    source.loop = loop;
    const gain = c.createGain();
    gain.gain.value = 1;
    source.connect(gain);
    gain.connect(c.destination);
    return source;
  } catch {
    return null;
  }
}

function stopSource(s: AudioBufferSourceNode | undefined): void {
  if (!s) return;
  try {
    s.stop();
  } catch {
    // Never started, or already stopped.
  }
  try {
    s.disconnect();
  } catch {
    // ignore
  }
}

export function isArmed(): boolean {
  return ctx !== null && ctx.state === 'running';
}

/** Subscribe to armed changes (AudioContext statechange). Returns the unsubscribe. */
export function onArmedChange(fn: (armed: boolean) => void): () => void {
  armedListeners.add(fn);
  return () => {
    armedListeners.delete(fn);
  };
}

/**
 * Call synchronously inside a user-gesture handler: creates/resumes the context and plays one silent sample
 * (the iOS/Safari unlock). Resolves whether the context is running.
 */
export async function arm(): Promise<boolean> {
  const c = ensureContext();
  if (!c) return false;
  try {
    const silent = c.createBuffer(1, 1, c.sampleRate);
    const s = c.createBufferSource();
    s.buffer = silent;
    s.connect(c.destination);
    s.start(0);
  } catch {
    // ignore: resume() below is what matters on Chrome and Edge
  }
  try {
    await c.resume();
  } catch {
    // Blocked: the state stays 'suspended'.
  }
  const armed = isArmed();
  for (const fn of armedListeners) fn(armed);
  return armed;
}

/** Loops the sound now. Idempotent: an equal sound already playing (or a scheduled one that started) is kept. */
export function startLoop(kind: AlarmSoundKind): boolean {
  const c = ensureContext();
  if (!c || !isArmed()) return false;
  if (playing && playing.kind === kind) return true;
  if (scheduled && scheduled.kind === kind && scheduled.startAt <= c.currentTime) {
    playing = { source: scheduled.source, kind };
    scheduled = null;
    return true;
  }
  stopSource(playing?.source);
  playing = null;
  const s = makeSource(c, kind, true);
  if (!s) return false;
  try {
    s.start(0);
    playing = { source: s, kind };
    return true;
  } catch {
    return false;
  }
}

/** Pre-schedules the loop `delaySec` from now on the audio clock (≤ 10 min). Replaces any earlier schedule. */
export function scheduleLoop(kind: AlarmSoundKind, delaySec: number): boolean {
  const c = ensureContext();
  if (!c || !isArmed() || delaySec < 0 || delaySec > MAX_SCHEDULE_DELAY_SEC) return false;
  const startAt = c.currentTime + delaySec;
  if (scheduled && scheduled.kind === kind && Math.abs(scheduled.startAt - startAt) < 0.5) return true;
  cancelScheduled();
  const s = makeSource(c, kind, true);
  if (!s) return false;
  try {
    s.start(startAt);
    scheduled = { source: s, kind, startAt };
    return true;
  } catch {
    return false;
  }
}

/** The audio-clock start of a pre-scheduled source that is already sounding, as seconds ago; else null. */
export function scheduledStartedSecondsAgo(): number | null {
  if (!ctx || !scheduled) return null;
  const ago = ctx.currentTime - scheduled.startAt;
  return ago >= 0 ? ago : null;
}

export function cancelScheduled(): void {
  stopSource(scheduled?.source);
  scheduled = null;
}

/** Stops the playing and the scheduled sources. */
export function stopLoop(): void {
  stopSource(playing?.source);
  playing = null;
  cancelScheduled();
}

export function isLooping(): boolean {
  return playing !== null;
}

/** arm() then one 2 s play. Resolves false if the context isn't running (the "Sound is blocked" banner). */
export async function playTest(kind: AlarmSoundKind): Promise<boolean> {
  const armedPromise = arm();
  const ok = await armedPromise;
  const c = ctx;
  if (!ok || !c) return false;
  const s = makeSource(c, kind, true);
  if (!s) return false;
  try {
    s.start(0);
    s.stop(c.currentTime + TEST_PLAY_SEC);
    return true;
  } catch {
    return false;
  }
}

/** Test seam: forget the context and every source. */
export function resetAlarmAudioForTests(): void {
  stopLoop();
  ctx = null;
  buffers.clear();
  armedListeners.clear();
}
